import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  defaultColDef,
  defaultGridOptionsFactory,
} from '@wilson/ag-grid/configuration';
import { LocalizedDateRendererComponent } from '@wilson/ag-grid/renderers';
import { ValueFormatterService } from '@wilson/ag-grid/service';
import { UserQualificationV2History } from '@wilson/interfaces';
import { ToQualificationCategoryPipe } from '@wilson/qualification-categories';
import { UserQualificationV2TransformValidTillPipe } from '@wilson/qualification-v2/common';
import { ShowTextTooltipRendererComponent } from '@wilson/qualifications/core';
import {
  DateRendererComponent,
  QualificationNameDateRendererComponent,
} from '@wilson/shared-employees';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, ValueFormatterParams } from 'ag-grid-community';
import { ConfirmedByUserRendererComponent } from '../qualifications-v2-list/renderers/confirmed-by-user-renderer';
import { QualificationLastEditRendererComponent } from './renderers/qualification-last-edit.renderer';

@Component({
  selector: 'wilson-qualification-version-list',
  templateUrl: './qualification-version-list.component.html',
  styleUrls: ['./qualification-version-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, AgGridModule],
  providers: [
    ToQualificationCategoryPipe,
    UserQualificationV2TransformValidTillPipe,
  ],
})
export class QualificationVersionListComponent {
  readonly valueFormatterService = inject(ValueFormatterService);
  readonly toQualificationCategoryPipe = inject(ToQualificationCategoryPipe);
  private translateService = inject(TranslateService);
  private readonly userQualificationV2TransformValidTillPipe = inject(
    UserQualificationV2TransformValidTillPipe,
  );

  @Input() qualificationVersions!:
    | UserQualificationV2History[]
    | null
    | undefined;

  public readonly defaultColDef = defaultColDef;
  public readonly gridOptions = defaultGridOptionsFactory();

  public readonly components = {
    confirmedByUserRendererComponent: ConfirmedByUserRendererComponent,
    dateRenderer: DateRendererComponent,
    nameDateRenderer: QualificationNameDateRendererComponent,
    showTextTooltipRendererComponent: ShowTextTooltipRendererComponent,
    qualificationLastEditRendererComponent:
      QualificationLastEditRendererComponent,
    localizedDateRendererComponent: LocalizedDateRendererComponent,
  };

  userId!: string;
  rootOrgUnitId!: string;
  public parentRoute = '';

  private gridApi!: GridApi;

  public columnDefs: ColDef[] = [
    {
      flex: 1,
      sortable: false,
      field: 'acquiredAt',
      cellRenderer: 'localizedDateRendererComponent',
      headerName: this.translateService.instant('general.acquired_on'),
    },
    {
      flex: 1,
      sortable: false,
      field: 'validFrom',
      cellRenderer: 'localizedDateRendererComponent',
      headerName: this.translateService.instant('general.valid_from'),
    },
    {
      flex: 1,
      sortable: false,
      field: 'validTill',
      headerName: this.translateService.instant('general.valid_until'),
      valueFormatter: (params: ValueFormatterParams): string => {
        return this.userQualificationV2TransformValidTillPipe.transform({
          validTill: params.value,
        });
      },
    },
    {
      flex: 1,
      sortable: false,
      cellRenderer: 'qualificationLastEditRendererComponent',
      headerName: this.translateService.instant('general.last_edit'),
    },
    {
      flex: 1,
      sortable: false,
      field: 'confirmedByUser',
      cellRenderer: 'confirmedByUserRendererComponent',
      headerName: this.translateService.instant(
        'page.qualifications.confirmed',
      ),
    },
    {
      flex: 1,
      sortable: false,
      field: 'notes',
      cellRenderer: 'showTextTooltipRendererComponent',
      headerName: this.translateService.instant('page.qualifications.notes'),
    },
  ];

  onGridReady(params: { api: GridApi }): void {
    this.gridApi = params.api;

    this.gridApi.sizeColumnsToFit();

    if (this.qualificationVersions) {
      this.gridApi.setGridOption('rowData', this.qualificationVersions);
    }
  }
}
